import React, { useState } from 'react';
import PropTypes from 'prop-types';

import LanguageConverter from '@careerstart/wae-common/src/main/helperFunction/LanguageConverter';
import { FormControlLabel, RadioGroup } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Radio from '@mui/material/Radio';
import Typography from '@mui/material/Typography';

import NumberIncrementDecrement from '../../../components/NumberIncrementDecrement';
import theme from '../../../theme';
import { epochToDateInReadableFormat } from '../../../utils';
import JOB_TYPE from '../constants';
import { createJobOrderDetailLeftSharedStyles } from '../jobOrderDetailStyles';

const { titleStyle, boldText, subTitleStyle, subText, buttonStyle } =
  createJobOrderDetailLeftSharedStyles(theme);

const OverstaffOpenings = ({
  jobStart,
  extGroupStart,
  extGroupEnd,
  capitalizedDays,
  overstaffInitialValue,
  extGroupOverstaffInitialValue,
}) => {
  const [overStaffOption, setOverstaffOption] = useState(JOB_TYPE.THIS_SHIFT);
  return (
    <Box data-testid="overstaffOpeningsBox">
      <Typography sx={{ ...titleStyle, marginTop: 3 }}>
        {LanguageConverter('jobDetails.changeOverstaffSettings')}
      </Typography>
      <Typography sx={subTitleStyle}>
        {extGroupStart
          ? LanguageConverter('jobDetails.changeOptions.overStaff')
          : LanguageConverter('jobDetails.changeShift')}
      </Typography>
      <FormControl component="fieldset">
        <RadioGroup
          aria-label="options"
          name="options"
          value={overStaffOption}
          onChange={(e) => setOverstaffOption(e.target.value)}
          orientation="vertical"
        >
          <FormControlLabel
            value={JOB_TYPE.THIS_SHIFT}
            control={<Radio />}
            label={
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  padding: 1,
                  borderColor: 'grey.300',
                  borderRadius: 1,
                  color:
                    overStaffOption === JOB_TYPE.THIS_SHIFT ? 'text.primary' : 'text.secondary',
                }}
              >
                <Box sx={boldText}>{LanguageConverter('jobDetails.thisShift')}</Box>
                <Box>{epochToDateInReadableFormat(jobStart)}</Box>
                {overStaffOption === JOB_TYPE.THIS_SHIFT && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      mt: 2,
                    }}
                  >
                    <Typography sx={{ ...subText, color: 'primary.main', fontWeight: 'bold' }}>
                      {LanguageConverter('jobDetails.openings')}
                    </Typography>
                    <NumberIncrementDecrement
                      value={overstaffInitialValue || 0}
                      onChange={() => {}}
                      min={0}
                      max={10000}
                      disabled
                      testId="newSingleOverstaff"
                    />
                  </Box>
                )}
              </Box>
            }
          />
          {extGroupStart ? (
            <FormControlLabel
              value={JOB_TYPE.ENTIRE_SERIES}
              control={<Radio />}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    padding: 1,
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    color:
                      overStaffOption === JOB_TYPE.ENTIRE_SERIES
                        ? 'text.primary'
                        : 'text.secondary',
                  }}
                >
                  <Box sx={boldText}>{LanguageConverter('jobDetails.series')}</Box>
                  <Box sx={subTitleStyle}>
                    {epochToDateInReadableFormat(extGroupStart)}-
                    {extGroupEnd
                      ? epochToDateInReadableFormat(extGroupEnd)
                      : LanguageConverter('jobDetails.ongoing')}
                  </Box>
                  <Box sx={subText}>{capitalizedDays?.join(', ')}</Box>
                  {overStaffOption !== JOB_TYPE.THIS_SHIFT && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        mt: 2,
                      }}
                    >
                      <Typography sx={{ ...subText, color: 'primary.main', fontWeight: 'bold' }}>
                        {LanguageConverter('jobDetails.openings')}
                      </Typography>
                      <NumberIncrementDecrement
                        value={extGroupOverstaffInitialValue || 0}
                        onChange={() => {}}
                        min={0}
                        max={10000}
                        disabled
                        testId="newGroupOverstaff"
                      />
                    </Box>
                  )}
                </Box>
              }
            />
          ) : null}
        </RadioGroup>
      </FormControl>
      <Box>
        <Button disabled variant="outlined" sx={buttonStyle}>
          {LanguageConverter('button.overstaff.update')}
        </Button>
      </Box>
    </Box>
  );
};

export default OverstaffOpenings;

OverstaffOpenings.propTypes = {
  jobStart: PropTypes.number,
  extGroupStart: PropTypes.number,
  extGroupEnd: PropTypes.number,
  capitalizedDays: PropTypes.arrayOf(PropTypes.string),
  overstaffInitialValue: PropTypes.number,
  extGroupOverstaffInitialValue: PropTypes.number,
};
